/**
 * Admin Config Defaults & Registry
 *
 * Single source of truth for known config keys, their display metadata, and
 * the default value used both as a runtime fallback (when the Dataverse row
 * doesn't exist) and as the seed value when an admin clicks "Initialize" in
 * the AdminConfigPage to create the row.
 *
 * Adding a new config:
 *   1. Add the key + payload type to ConfigKeyMap (types.ts)
 *   2. Add an entry here with category / displayName / description / default
 *   3. (Optional) write a specialized editor and register it in
 *      catalog/admin-config/AdminConfigPage.tsx EDITORS
 */

import type {
  ConfigKey,
  ConfigKeyMap,
  SalesStagesValue,
  SolutionCategoriesValue,
  SalesYearValue,
  RepTeamsValue,
  OutreachTemplateValue,
  OneCardOutcomeTagsValue,
  PointsMatrixValue,
  HelpPanelValue,
  IntakeMappingsValue,
  TeammateRolesValue,
  TaskStatusColorsValue,
  LinksquaresIgnoredTagsValue,
  LauncherCategoriesValue,
  LargeCaseAlertValue,
} from './types';
import { DEFAULT_FOGHORN_CONFIG } from '../../../lib/daylight/foghornSchedule';
// The DATA module, not moduleCatalog.ts: these defaults are bundled into the
// reports-scheduler Function App (fetchers import CONFIG_DEFAULTS), and the
// hook file chains into AuthProvider -> config/env, whose import.meta read
// crashes a CJS bundle at load (took the scheduler down 2026-08-08).
import { MODULE_CATALOG, MODULE_CATEGORIES } from '../../../ui/components/moduleCatalogData';

const LINKSQUARES_IGNORED_TAGS_DEFAULT: LinksquaresIgnoredTagsValue = {
  tagNames: [],
};

/**
 * Launcher categories default, derived from the code-level catalog so the
 * built-in grouping and the admin-managed grouping can never start out of
 * sync. Exported as the runtime fallback for useLauncherCategories().
 */
export const LAUNCHER_CATEGORIES_DEFAULT: LauncherCategoriesValue = {
  categories: MODULE_CATEGORIES.map((c) => ({
    id: c.id,
    label: c.label,
    color: c.color,
    ...(c.foreground ? { foreground: c.foreground } : {}),
  })),
  assignments: Object.fromEntries(MODULE_CATALOG.map((m) => [m.id, m.category])),
};

interface ConfigDefault<K extends ConfigKey> {
  key: K;
  category: string;
  displayName: string;
  description: string;
  value: ConfigKeyMap[K];
}

const SALES_STAGES_DEFAULT: SalesStagesValue = {
  stages: [
    { name: 'Working', isCase: false, isPipeline: true, color: null, order: 0, bpfStageId: '5a47c4aa-5647-4bfd-afd2-6f0bcb12a794' },
    { name: 'Qualify', isCase: false, isPipeline: true, color: null, order: 1, bpfStageId: 'd3ded8f3-2e7b-475a-8c9b-be96bacb8cf9' },
    { name: 'Solution Scoping', isCase: false, isPipeline: true, color: '#3F4C5C', order: 2, bpfStageId: 'c251728c-ff71-4919-9490-a4999bc3c206' },
    { name: 'Proposal Creation For Open Case', isCase: true, isPipeline: false, color: '#F79E31', order: 3, bpfStageId: 'c248197f-0cf7-4851-85d3-a605a165bf46' },
    { name: 'Presentation', isCase: true, isPipeline: false, color: '#597B4B', order: 4, bpfStageId: '2d945b11-4433-4e44-bda9-633b7ec41ba7' },
    { name: 'Finalization', isCase: false, isPipeline: false, color: null, order: 5, mappedTo: 'Presentation' },
  ],
};

// Default: each category is 1:1 with a Catalog solution so the intake drawer
// can resolve quoted/sold opportunities to the right seedable solution. Use
// the Solution Categories editor in Catalog admin to adjust mappings when
// products change.
const SOLUTION_CATEGORIES_DEFAULT: SolutionCategoriesValue = {
  categories: [
    {
      name: 'Health Plan Solutions',
      fields: ['dts_tpaadmin'],
      soldLabels: ['TPA Services'],
      order: 0,
      color: '#553746',
    },
    {
      name: 'Veracity Rx',
      fields: ['dts_veracityrx'],
      soldLabels: ['VeracityRx'],
      order: 1,
      color: '#D6461D',
    },
    {
      name: 'Onsite Health Centers',
      fields: ['dts_veracityonsite'],
      soldLabels: ['Onsite Health Centers'],
      order: 2,
      color: '#3F4C5C',
    },
    {
      name: 'Risk Solutions',
      fields: ['dts_veracityrisk'],
      soldLabels: ['Risk Solutions'],
      order: 3,
      color: '#EE6352',
    },
    {
      name: 'Care Solutions',
      fields: [],
      soldLabels: ['Care Solutions'],
      order: 4,
      color: '#553746',
    },
    {
      name: 'Providers of Excellence',
      fields: ['dts_providersofexcellence'],
      soldLabels: ['Providers of Excellence'],
      order: 5,
      color: '#553746',
    },
    {
      name: 'MEC',
      fields: ['dts_minimumessentialcoveragemec'],
      soldLabels: ['MEC'],
      order: 6,
      color: '#597B4B',
    },
    {
      name: 'Maplebridge',
      fields: ['dts_maplebridge'],
      soldLabels: ['Maplebridge'],
      order: 7,
      color: '#3F4C5C',
    },
  ],
  fieldLabels: {
    dts_tpaadmin: 'TPA Admin',
    dts_veracityrx: 'Veracity Rx',
    dts_veracityonsite: 'Veracity OnSite',
    dts_veracityrisk: 'Veracity Risk',
    dts_providersofexcellence: 'Providers of Excellence',
    dts_minimumessentialcoveragemec: 'MEC',
    dts_maplebridge: 'Maplebridge',
  },
};

const SALES_YEAR_DEFAULT: SalesYearValue = { startMonth: 2 };

// Ships disabled with nobody on it: a human picks the recipients from the
// Sales Admin page before the first email ever goes out.
const LARGE_CASE_ALERT_DEFAULT: LargeCaseAlertValue = {
  enabled: false,
  threshold: 1000,
  recipients: [],
  includeRenewals: false,
};

const REP_TEAMS_DEFAULT: RepTeamsValue = { teams: [] };

const OUTREACH_TEMPLATE_DEFAULT: OutreachTemplateValue = { templates: {} };

const ONE_CARD_OUTCOME_TAGS_DEFAULT: OneCardOutcomeTagsValue = {
  tags: [
    { label: 'Outreach initiated', order: 0 },
    { label: 'Outreach completed — no new opportunity', order: 1 },
    { label: 'Broker lost group', order: 2 },
    { label: 'Meeting scheduled', order: 3 },
    { label: 'Quote requested', order: 4 },
  ],
};

const HELP_PANEL_DEFAULT: HelpPanelValue = {
  sections: [
    {
      id: 'docs',
      title: 'Documentation',
      links: [
        {
          id: 'user-guide',
          label: 'User Guide',
          description: 'Learn how to use this app',
          href: '/guide',
          icon: 'book',
          enabled: true,
        },
      ],
    },
    {
      id: 'support',
      title: 'Contact Support',
      links: [
        {
          id: 'email-support',
          label: 'Email Support',
          description: 'support@oneveracity.com',
          href: 'mailto:support@oneveracity.com',
          icon: 'mail',
          enabled: true,
        },
        {
          id: 'live-chat',
          label: 'Live Chat',
          description: 'Coming soon',
          href: '',
          icon: 'chat',
          enabled: false,
        },
      ],
    },
  ],
};

const INTAKE_MAPPINGS_DEFAULT: IntakeMappingsValue = { mappings: [] };

const TEAMMATE_ROLES_DEFAULT: TeammateRolesValue = {
  roles: [
    { id: 'implementation-manager', label: 'Implementation Manager', active: true, order: 0 },
    { id: 'data-manager', label: 'Data Manager', active: true, order: 1 },
    { id: 'secondary-salesperson', label: 'Secondary Salesperson', active: true, order: 2 },
  ],
};

const POINTS_MATRIX_DEFAULT: PointsMatrixValue = {
  unknownBrokerDefault: 'prospect',
  schedules: [
    {
      id: 'default',
      startDate: '2026-01-01',
      endDate: null,
      weights: {
        email: { existing: 0.25, prospect: 0.5 },
        phonecall: { existing: 0.5, prospect: 1 },
        appointmentVirtual: { existing: 1, prospect: 2 },
        appointmentFaceToFace: { existing: 3, prospect: 5 },
        appointmentMeal: { existing: 3, prospect: 5 },
        proposalCreated: { existing: 3, prospect: 5 },
        proposalPresented: { existing: 5, prospect: 10 },
      },
    },
  ],
};

// Canonical implementation task statuses (Dataverse option-set values on
// vba_impprojecttasks.vba_impstatus) with their default Gantt colors. The
// alias 100000004 (Waiting on Client/Broker) is intentionally omitted — it's
// hidden from the UI. Admins can recolor these in Catalog → Implementation
// template Settings; the set itself is fixed by the option-set.
const TASK_STATUS_COLORS_DEFAULT: TaskStatusColorsValue = {
  statuses: [
    { value: 100000000, name: 'Not Started', color: '#c4a98a', category: 'Open' },
    { value: 100000001, name: 'In Progress', color: '#3F4C5C', category: 'In Progress' },
    { value: 100000002, name: 'Submitted', color: '#597B4B', category: 'In Progress' },
    { value: 100000003, name: 'Waiting on Client/Broker', color: '#F79E31', category: 'In Progress' },
    { value: 100000005, name: 'Waiting on TPA', color: '#F79E31', category: 'In Progress' },
    { value: 100000006, name: 'Waiting on Rx', color: '#F79E31', category: 'In Progress' },
    { value: 100000007, name: 'Waiting on Vendor', color: '#F79E31', category: 'In Progress' },
    { value: 100000008, name: 'Complete', color: '#597B4B', category: 'Done' },
    { value: 100000009, name: 'Not Needed', color: '#a8896b', category: 'Done' },
    { value: 100000010, name: 'Blocked', color: '#EE6352', category: 'Blocked' },
  ],
};

export const CONFIG_DEFAULTS: { [K in ConfigKey]: ConfigDefault<K> } = {
  'sales.stages': {
    key: 'sales.stages',
    category: 'Sales',
    displayName: 'Pipeline Stages',
    description: 'Sales pipeline stage definitions — controls which stages are classified as Early Pipeline or Proposal Pipeline (Cases)',
    value: SALES_STAGES_DEFAULT,
  },
  'sales.solutionCategories': {
    key: 'sales.solutionCategories',
    category: 'Sales',
    displayName: 'Sales Solution Categories',
    description: 'Solution-category names, sort order, and colors. Pills throughout Vera Sales (Pipeline Open Cases, Won Cases, Broker Scorecard) inherit color from the product’s category.',
    value: SOLUTION_CATEGORIES_DEFAULT,
  },
  'sales.salesYear': {
    key: 'sales.salesYear',
    category: 'Sales',
    displayName: 'Sales Year',
    description: 'Start month for the sales year (YTD calculations).',
    value: SALES_YEAR_DEFAULT,
  },
  'sales.repTeams': {
    key: 'sales.repTeams',
    category: 'Sales',
    displayName: 'Rep Teams',
    description: 'Groups multiple salespeople under a single reporting name',
    value: REP_TEAMS_DEFAULT,
  },
  'sales.outreachTemplate': {
    key: 'sales.outreachTemplate',
    category: 'Sales',
    displayName: 'Outreach Email Template',
    description: 'Email template used by One Card outreach button',
    value: OUTREACH_TEMPLATE_DEFAULT,
  },
  'sales.oneCardOutcomeTags': {
    key: 'sales.oneCardOutcomeTags',
    category: 'Sales',
    displayName: 'One Card Outcome Tags',
    description: 'Tag options shown in the "Log outreach" dialog on the One Card page. Each saved tag + note is appended as a new annotation on the group\'s CRM activity timeline.',
    value: ONE_CARD_OUTCOME_TAGS_DEFAULT,
  },
  'sales.largeCaseAlert': {
    key: 'sales.largeCaseAlert',
    category: 'Sales',
    displayName: 'Large Case Alert',
    description:
      'Daily email alert when an opportunity at or above the configured lives threshold is created or first enters a proposal stage. Threshold and recipients are edited on the Sales Admin page; the reports-scheduler Function App runs the check.',
    value: LARGE_CASE_ALERT_DEFAULT,
  },
  'sales.pointsMatrix': {
    key: 'sales.pointsMatrix',
    category: 'Sales',
    displayName: 'Activity Points Matrix',
    description: 'Time-gated point weights per activity type, split by Existing vs Prospect broker. Drives the Points view on the Activity Report.',
    value: POINTS_MATRIX_DEFAULT,
  },
  'shell.helpPanel': {
    key: 'shell.helpPanel',
    category: 'Shell',
    displayName: 'Help & Support Panel',
    description: 'Sections and links shown in the in-app Help & Support modal (opened from the ? icon in every app header).',
    value: HELP_PANEL_DEFAULT,
  },
  'shell.launcherCategories': {
    key: 'shell.launcherCategories',
    category: 'Shell',
    displayName: 'Launcher Categories',
    description:
      'Home page organization: section order, labels, brand colors, and which section each module renders under. Colors must be approved brand-guide combinations (light colors carry a Midnight Charcoal foreground per section 4.5).',
    value: LAUNCHER_CATEGORIES_DEFAULT,
  },
  'intake.fieldMappings': {
    key: 'intake.fieldMappings',
    category: 'Ops',
    displayName: 'Intake Field Mappings',
    description: 'Maps Dynamics opportunity / close-form / account fields onto Catalog GI fields and service options. After stamping solutions for a new group, the Enrollment Drawer pops a review modal showing each mapped value and asks the IC to confirm or override.',
    value: INTAKE_MAPPINGS_DEFAULT,
  },
  'ops.foghorn': {
    key: 'ops.foghorn',
    category: 'Ops',
    displayName: 'The Foghorn',
    description: 'Cadence for the Operation Daylight bulletin posted to Microsoft Teams (off, daily, weekly, or monthly, at an hour in Eastern time). Set from the Foghorn dialog on the Operation Daylight page; the target channel is the webhook configured on the reports-scheduler Function App.',
    value: DEFAULT_FOGHORN_CONFIG,
  },
  'ops.teammateRoles': {
    key: 'ops.teammateRoles',
    category: 'Ops',
    displayName: 'Teammate Roles',
    description: 'Master list of operational roles a Veracity teammate can hold on a group (e.g. Implementation Manager, Data Manager, Secondary Salesperson). Managed here; assigned per-group from the Team card on the group detail page. The Account Exec (Dynamics account owner) and the Primary Salesperson (Dynamics opportunity owner) live only in Dynamics and are not in this list.',
    value: TEAMMATE_ROLES_DEFAULT,
  },
  'implementation.taskStatusColors': {
    key: 'implementation.taskStatusColors',
    category: 'Ops',
    displayName: 'Task Status Colors',
    description: 'Per-status colors for implementation project tasks (Gantt bars + status badges). The status set is fixed by the Dataverse option-set; this overrides only the colors. Edit from a Catalog implementation template’s Settings tab.',
    value: TASK_STATUS_COLORS_DEFAULT,
  },
  'linksquares.ignoredTags': {
    key: 'linksquares.ignoredTags',
    category: 'Catalog',
    displayName: 'LinkSquares Ignored Tags',
    description: 'Tag names hidden from the LinkSquares mapping workbench\'s Unclaimed Tags list (year tags, status tags, vendor tags, our own entities). Managed from the workbench itself via the Ignore action; listed here for visibility and bulk cleanup.',
    value: LINKSQUARES_IGNORED_TAGS_DEFAULT,
  },
};

export const CONFIG_KEYS = Object.keys(CONFIG_DEFAULTS) as ConfigKey[];

export function getConfigDefault<K extends ConfigKey>(key: K): ConfigDefault<K> {
  return CONFIG_DEFAULTS[key];
}
